import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Move, Plus } from 'lucide-react';
import { maquetaService, plantillaArticuloService, storageService } from '../../services/api';
import EditorMultimedia from './EditorMultimedia';
import { uploadImage, resizeImage } from '../../services/storage';

/**
 * PaginaCanvas — renderiza el grid de una página y los bloques colocados.
 *
 * Grid: proporción tabloide (28×43 cm ⇒ ratio 0.651). El canvas se dimensiona
 * al ancho disponible manteniendo esa proporción.
 *
 * Interacciones:
 *   - Drop de artículo/pauta desde la sidebar → crea bloque en la celda.
 *   - Drag del propio bloque → mueve (respeta snap a celda).
 *   - Handle esquina inferior-derecha → redimensiona (snap a celda).
 *   - X en el bloque → elimina.
 *
 * Todos los cambios se persisten inmediatamente. Si el backend rechaza
 * (solapamiento o fuera del grid), se hace rollback local y se muestra alert.
 */
export default function PaginaCanvas({ onFocoBloque, pagina, onCambio }) {
  const [bloques, setBloques] = useState(pagina.bloques);
  const contenedorRef = useRef(null);
  const [dims, setDims] = useState({ ancho: 0, alto: 0, celdaAncho: 0, celdaAlto: 0 });
  const [tiposDinamicos, setTiposDinamicos] = useState([]);

  const [pickerDinamicoAbierto, setPickerDinamicoAbierto] = useState(false);
  // Factor de zoom del canvas (1 = ajuste a viewport, 2 = doble tamaño, etc.)
  const [zoom, setZoom] = useState(1);

  // Cargar catálogo de tipos de contenido dinámico una sola vez
  useEffect(() => {
    (async () => {
      try {
        const tipos = await maquetaService.listarTiposDinamicos();
        setTiposDinamicos(tipos);
      } catch { /* fallback: sin tipos disponibles */ }
    })();
  }, []);

  // Dispara toast global tras cada operación exitosa. El EditionEditorPage
  // escucha 'app:toast' y muestra el pill verde flotante durante 1.5s.
  const toast = (msg) => {
    window.dispatchEvent(new CustomEvent('app:toast', { detail: msg }));
  };

  // Ref que siempre apunta al valor MÁS RECIENTE de bloques. Necesario porque
  // los closures registrados en useEffect (window.addEventListener) capturan
  // bloques en el momento del registro y no ven las actualizaciones locales
  // del drag. Sin esto, al soltar el mouse se persistía la posición ORIGINAL,
  // el backend quedaba con datos viejos y el bloque "saltaba" al recargar.
  const bloquesRef = useRef(bloques);
  useEffect(() => { bloquesRef.current = bloques; }, [bloques]);

  // Plantillas de artículo disponibles (para el dropdown en cada bloque).
  const [plantillasArt, setPlantillasArt] = useState([]);
  useEffect(() => {
    plantillaArticuloService.listar(true)
      .then(list => setPlantillasArt(Array.isArray(list) ? list : []))
      .catch(() => setPlantillasArt([]));
  }, []);

  // Cambiar la plantilla asignada a un bloque
  const cambiarPlantillaBloque = async (b, plantillaId) => {
    try {
      const actualizado = await maquetaService.actualizarBloque(b.id, {
        columnaInicio: b.columnaInicio, filaInicio: b.filaInicio,
        columnasSpan: b.columnasSpan, filasSpan: b.filasSpan,
        articuloId: b.articuloId ?? null, pautaId: b.pautaId ?? null,
        elementoData: b.elementoData ?? null, estilo: b.estilo ?? null,
        plantillaArticuloId: plantillaId ?? null,
      });
      setBloques(prev => prev.map(x => x.id === b.id ? actualizado : x));
      onCambio?.();
      toast("Layout actualizado");
    } catch (err) {
      alert('No se pudo aplicar la plantilla: ' + err.message);
    }
  };

  // Sincronizar cuando cambia la página desde afuera
  useEffect(() => { setBloques(pagina.bloques); }, [pagina.bloques, pagina.id]);

  // Calcular dimensiones del canvas manteniendo proporción tabloide 28×43
  useEffect(() => {
    const compute = () => {
      const el = contenedorRef.current;
      if (!el) return;
      const anchoDisp = el.clientWidth;
      const altoDisp  = el.clientHeight;
      const ratio = 28 / 43; // ancho/alto tabloide
      let ancho = anchoDisp, alto = anchoDisp / ratio;
      if (alto > altoDisp) { alto = altoDisp; ancho = altoDisp * ratio; }
      // Aplicar zoom: 1 = ajuste a viewport, 2 = doble tamaño, etc.
      ancho *= zoom;
      alto  *= zoom;
      setDims({
        ancho, alto,
        celdaAncho: ancho / pagina.columnas,
        celdaAlto:  alto  / pagina.filas,
      });
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (contenedorRef.current) ro.observe(contenedorRef.current);
    return () => ro.disconnect();
  }, [pagina.columnas, pagina.filas, zoom]);

  // Convierte coordenadas pixel dentro del canvas a (columna, fila) del grid
  const pixelACelda = (px, py) => {
    const col  = Math.max(1, Math.min(pagina.columnas, Math.floor(px / dims.celdaAncho) + 1));
    const fila = Math.max(1, Math.min(pagina.filas,    Math.floor(py / dims.celdaAlto)  + 1));
    return { col, fila };
  };

  // ─── Drop desde la sidebar ────────────────────────────────────────
  const onDragOver = (e) => { e.preventDefault(); };
  const onDrop = async (e) => {
    e.preventDefault();
    const canvas = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - canvas.left;
    const py = e.clientY - canvas.top;
    const { col, fila } = pixelACelda(px, py);

    let data;
    try { data = JSON.parse(e.dataTransfer.getData('text/plain') || '{}'); } catch { return; }
    if (!data.tipo) return;

    // Caso 1: sueltas dentro de un bloque SLOT vacío existente (sin contenido).
    // Ejemplo: aplicaste la plantilla "Portada clásica" y arrastras un artículo
    // al slot Hero. En vez de crear un bloque nuevo (colisión), rellenamos
    // el slot existente con el articuloId/pautaId. Respeta el tipo del slot.
    const slotDestino = bloques.find(b =>
      col >= b.columnaInicio && col < b.columnaInicio + b.columnasSpan &&
      fila >= b.filaInicio && fila < b.filaInicio + b.filasSpan &&
      !b.articuloId && !b.pautaId
    );
    if (slotDestino) {
      try {
        const actualizado = await maquetaService.actualizarBloque(slotDestino.id, {
          columnaInicio: slotDestino.columnaInicio, filaInicio: slotDestino.filaInicio,
          columnasSpan:  slotDestino.columnasSpan,  filasSpan:   slotDestino.filasSpan,
          articuloId: data.articuloId ?? null,
          pautaId:    data.pautaId    ?? null,
          elementoData: slotDestino.elementoData ?? null,
          estilo:       slotDestino.estilo ?? null,
        });
        setBloques(prev => prev.map(b => b.id === slotDestino.id ? actualizado : b));
        onCambio?.();
        toast("Artículo asignado");
      } catch (err) {
        alert('No se pudo asignar al slot: ' + err.message);
      }
      return;
    }

    // Caso 2: espacio realmente libre — crear bloque nuevo. Intentamos con
    // 2×3 (default), y si no cabe en la posición del drop probamos tamaños
    // más pequeños. Si aun así no cabe, buscamos hueco libre en toda la
    // página (no solo en el punto del drop).
    const cabe = (c, f, w, h) => {
      if (c + w - 1 > pagina.columnas || f + h - 1 > pagina.filas) return false;
      return !bloques.some(b =>
        !(c + w - 1 < b.columnaInicio
        || c > b.columnaInicio + b.columnasSpan - 1
        || f + h - 1 < b.filaInicio
        || f > b.filaInicio + b.filasSpan - 1)
      );
    };
    const tamaños = data.tipo === 'elemento_fijo'
      ? [[pagina.columnas, 1], [3, 1], [2, 1]]
      : [[2, 3], [2, 2], [1, 2], [1, 1]];

    let colOk = col, filOk = fila, wOk = null, hOk = null;
    for (const [w, h] of tamaños) {
      if (cabe(col, fila, w, h)) { wOk = w; hOk = h; break; }
    }
    // Si nada cabe en el punto exacto, buscar cualquier hueco libre.
    if (!wOk) {
      for (const [w, h] of tamaños) {
        const h1 = buscarHuecoLibre(w, h);
        if (h1) { colOk = h1.columna; filOk = h1.fila; wOk = w; hOk = h; break; }
      }
    }
    if (!wOk) {
      const celdasOc = bloques.reduce((s, b) => s + b.columnasSpan * b.filasSpan, 0);
      const celdasT  = pagina.columnas * pagina.filas;
      alert(
        `No hay un rectángulo libre para ese bloque.\n\n` +
        `Página: ${celdasT} celdas · Ocupadas: ${celdasOc} · Libres: ${celdasT - celdasOc}\n` +
        `Sugerencia: reduce el tamaño de algún bloque para liberar espacio contiguo.`
      );
      return;
    }
    const dto = {
      columnaInicio: colOk, filaInicio: filOk,
      columnasSpan: wOk, filasSpan: hOk,
      tipo: data.tipo,
      articuloId: data.articuloId ?? null,
      pautaId:    data.pautaId    ?? null,
      elementoData: null, estilo: null,
    };
    try {
      const nuevo = await maquetaService.crearBloque(pagina.id, dto);
      setBloques(prev => [...prev, nuevo]);
      onCambio?.();
      toast("Bloque creado");
    } catch (err) {
      alert('No se pudo colocar el bloque: ' + err.message);
    }
  };

  // ─── Drag & resize de bloques existentes ──────────────────────────
  const [drag, setDrag] = useState(null);
  // drag = { bloqueId, tipo: 'move'|'resize', startX, startY, orig: {col,fila,cs,fs} }

  const iniciarMove = (e, b) => {
    e.stopPropagation();
    setDrag({ bloqueId: b.id, tipo: 'move', startX: e.clientX, startY: e.clientY,
              orig: { col: b.columnaInicio, fila: b.filaInicio, cs: b.columnasSpan, fs: b.filasSpan } });
  };
  const iniciarResize = (e, b) => {
    e.stopPropagation();
    setDrag({ bloqueId: b.id, tipo: 'resize', startX: e.clientX, startY: e.clientY,
              orig: { col: b.columnaInicio, fila: b.filaInicio, cs: b.columnasSpan, fs: b.filasSpan } });
  };

  useEffect(() => {
    if (!drag) return;
    const onMove = (e) => {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      const deltaCol  = Math.round(dx / dims.celdaAncho);
      const deltaFila = Math.round(dy / dims.celdaAlto);

      setBloques(prev => prev.map(b => {
        if (b.id !== drag.bloqueId) return b;
        if (drag.tipo === 'move') {
          const c = Math.max(1, Math.min(pagina.columnas - drag.orig.cs + 1, drag.orig.col + deltaCol));
          const f = Math.max(1, Math.min(pagina.filas    - drag.orig.fs + 1, drag.orig.fila + deltaFila));
          return { ...b, columnaInicio: c, filaInicio: f };
        } else {
          const cs = Math.max(1, Math.min(pagina.columnas - drag.orig.col + 1, drag.orig.cs + deltaCol));
          const fs = Math.max(1, Math.min(pagina.filas    - drag.orig.fila + 1, drag.orig.fs + deltaFila));
          return { ...b, columnasSpan: cs, filasSpan: fs };
        }
      }));
    };
    const onUp = async () => {
      // Persistir el bloque afectado. Leemos del REF, no del state, para
      // ver la posición actualizada por el drag (los closures registrados en
      // useEffect no ven las actualizaciones intermedias de setBloques).
      const b = bloquesRef.current.find(x => x.id === drag.bloqueId);
      const dragCopy = drag;
      setDrag(null);
      if (!b) return;
      try {
        await maquetaService.actualizarBloque(b.id, {
          columnaInicio: b.columnaInicio, filaInicio: b.filaInicio,
          columnasSpan: b.columnasSpan,   filasSpan:   b.filasSpan,
          articuloId: b.articuloId ?? null, pautaId: b.pautaId ?? null,
          elementoData: b.elementoData ?? null, estilo: b.estilo ?? null,
        });
        onCambio?.();
        toast("Bloque actualizado");
      } catch (err) {
        alert('No se pudo mover/redimensionar: ' + err.message);
        // Rollback local a la posición original
        setBloques(prev => prev.map(x => x.id === dragCopy.bloqueId ? {
          ...x, columnaInicio: dragCopy.orig.col, filaInicio: dragCopy.orig.fila,
          columnasSpan: dragCopy.orig.cs, filasSpan: dragCopy.orig.fs,
        } : x));
      }
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag, dims, pagina.columnas, pagina.filas]);

  const eliminarBloque = async (bloqueId) => {
    if (!window.confirm('¿Eliminar este bloque?')) return;
    try {
      await maquetaService.eliminarBloque(bloqueId);
      setBloques(prev => prev.filter(b => b.id !== bloqueId));
      onCambio?.();
      toast("Bloque eliminado");
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  // ─── Crear bloque vacío desde la paleta ───────────────────────────
  // A diferencia del drop (que crea bloque con contenido asignado), acá
  // buscamos el primer hueco libre en el grid y colocamos un bloque sin
  // asignación. El editor asigna contenido luego con drag desde el sidebar.
  const buscarHuecoLibre = (ancho, alto) => {
    // Barrido de arriba a abajo, izquierda a derecha
    for (let f = 1; f <= pagina.filas - alto + 1; f++) {
      for (let c = 1; c <= pagina.columnas - ancho + 1; c++) {
        // Verificar que ningún bloque solape este rectángulo
        const solapa = bloques.some(b =>
          !(c + ancho - 1 < b.columnaInicio
          || c > b.columnaInicio + b.columnasSpan - 1
          || f + alto - 1 < b.filaInicio
          || f > b.filaInicio + b.filasSpan - 1)
        );
        if (!solapa) return { columna: c, fila: f };
      }
    }
    return null; // no cabe
  };

  const crearBloqueVacio = async (tipo, ancho, alto, tipoContenido, parametrosDefault) => {
    // Tamaños default por tipo, ajustados a lo que típicamente cabe:
    //   elemento_fijo → ancho completo, 1 fila (cabezote, folio, filete)
    //   pauta         → 2×3 (banner mediano)
    //   articulo      → 2×3
    //   dinamico      → 2×4 (más noticias, índice, etc.)
    let intentos;
    if (tipo === 'elemento_fijo') {
      intentos = [[pagina.columnas, 1], [3, 1], [2, 1]];
    } else if (tipo === 'pauta') {
      intentos = [[ancho ?? 2, alto ?? 3], [2, 2], [1, 2], [1, 1]];
    } else if (tipo === 'dinamico') {
      // cabezote ocupa todo el ancho, resto son cajas 2×4
      const preferido = tipoContenido === 'cabezote'
        ? [pagina.columnas, 2]
        : [ancho ?? 2, alto ?? 4];
      intentos = [preferido, [2, 3], [2, 2], [1, 2]];
    } else { // articulo
      intentos = [[ancho ?? 2, alto ?? 3], [2, 2], [1, 2], [1, 1]];
    }

    let hueco = null, usa = null;
    for (const [w, h] of intentos) {
      hueco = buscarHuecoLibre(w, h);
      if (hueco) { usa = [w, h]; break; }
    }
    if (!hueco || !usa) {
      // Mensaje útil: cuánto espacio real hay libre.
      const celdasOcupadas = bloques.reduce((s, b) => s + b.columnasSpan * b.filasSpan, 0);
      const celdasTotales  = pagina.columnas * pagina.filas;
      const libres = celdasTotales - celdasOcupadas;
      alert(
        `No hay un rectángulo libre para ese bloque.\n\n` +
        `Página: ${pagina.columnas}×${pagina.filas} = ${celdasTotales} celdas\n` +
        `Ocupadas: ${celdasOcupadas}\n` +
        `Libres: ${libres} (pero no contiguas en un rectángulo)\n\n` +
        `Sugerencia: mueve o redimensiona algún bloque para liberar espacio contiguo.`
      );
      return;
    }
    const dto = {
      columnaInicio: hueco.columna, filaInicio: hueco.fila,
      columnasSpan: usa[0], filasSpan: usa[1],
      tipo, articuloId: null, pautaId: null,
      elementoData: tipo === 'dinamico' && tipoContenido
        ? JSON.stringify({ tipoContenido, parametros: parametrosDefault || {} })
        : null,
      estilo: null,
    };
    try {
      const nuevo = await maquetaService.crearBloque(pagina.id, dto);
      setBloques(prev => [...prev, nuevo]);
      onCambio?.();
      toast("Bloque creado");
    } catch (err) {
      alert('No se pudo crear el bloque: ' + err.message);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────
  return (
    <div style={{ display:'flex', gap:12, width:'100%', height:'100%' }}>
      {/* Paleta lateral para añadir bloques al vuelo (igual que editor de
          plantillas). Sirve para ubicar pautas donde el editor decida sin
          depender de una plantilla predefinida. */}
      <div style={{ width:130, display:'flex', flexDirection:'column', gap:10,
                    flexShrink:0, alignSelf:'flex-start' }}>
      <div style={{
        display:'flex', flexDirection:'column', gap:6,
        background:'#fff', border:'1px solid #d1d5db', borderRadius:4, padding:8,
      }}>
        <div style={{ fontSize:11, color:'#666', fontWeight:600, marginBottom:4 }}>
          Añadir bloque
        </div>
        <button onClick={() => crearBloqueVacio('articulo')} style={{ ...btnAdd, borderColor:'#1B4332', color:'#1B4332' }}>
          <Plus size={12} /> Artículo
        </button>
        <button onClick={() => crearBloqueVacio('pauta')} style={{ ...btnAdd, borderColor:'#1e40af', color:'#1e40af' }}>
          <Plus size={12} /> Pauta
        </button>
        <button onClick={() => crearBloqueVacio('elemento_fijo')} style={{ ...btnAdd, borderColor:'#78350f', color:'#78350f' }}>
          <Plus size={12} /> Elemento fijo
        </button>
        {tiposDinamicos.length > 0 && (
          <button
            onClick={() => setPickerDinamicoAbierto(true)}
            style={{ ...btnAdd, borderColor:'#7c3aed', color:'#7c3aed' }}
          >
            <Plus size={12} /> Dinámico
          </button>
        )}
        {pickerDinamicoAbierto && (
          <div
            onClick={() => setPickerDinamicoAbierto(false)}
            style={{
              position:'fixed', inset:0, background:'rgba(0,0,0,0.4)',
              display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999,
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{
                background:'#fff', borderRadius:8, padding:20, minWidth:360, maxWidth:480,
                boxShadow:'0 20px 60px rgba(0,0,0,0.2)',
              }}
            >
              <div style={{ fontSize:16, fontWeight:700, color:'#1B4332', marginBottom:12 }}>
                Añadir bloque dinámico
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {tiposDinamicos.map(t => (
                  <button
                    key={t.codigo}
                    onClick={() => {
                      setPickerDinamicoAbierto(false);
                      crearBloqueVacio('dinamico', null, null, t.codigo, t.parametrosDefault || {});
                    }}
                    style={{
                      textAlign:'left', padding:'10px 12px', border:'1px solid #e5e7eb',
                      borderRadius:6, background:'#fff', cursor:'pointer',
                    }}
                  >
                    <div style={{ fontSize:13, fontWeight:700, color:'#7c3aed' }}>{t.nombre}</div>
                    {t.descripcion && (
                      <div style={{ fontSize:11, color:'#666', marginTop:2 }}>{t.descripcion}</div>
                    )}
                  </button>
                ))}
              </div>
              <div style={{ display:'flex', justifyContent:'flex-end', marginTop:14 }}>
                <button
                  onClick={() => setPickerDinamicoAbierto(false)}
                  style={{ padding:'6px 14px', background:'#e5e7eb', border:'none', borderRadius:4, cursor:'pointer' }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
        <div style={{ fontSize:10, color:'#888', marginTop:8, lineHeight:1.35 }}>
          El bloque aparece en el primer hueco libre. Arrastra su cabecera para moverlo, la esquina inferior derecha para redimensionar.
        </div>
      </div>

      {/* Tamaño de la página. Dato fijo: no cambia al mover el cursor, a
          diferencia de la caja de la barra superior, que muestra el bloque. */}
      <div style={{
        display:'flex', flexDirection:'column', gap:4,
        background:'#fff', border:'1px solid #d1d5db', borderRadius:4, padding:8,
      }}>
        <div style={{ fontSize:11, color:'#666', fontWeight:600, marginBottom:2 }}>
          Tamaño de página
        </div>
        {medidasDePagina(pagina).map(([etiqueta, valor]) => (
          <div key={etiqueta} style={{ display:'flex', flexDirection:'column' }}>
            <span style={{ fontSize:9, color:'#9a9a95', textTransform:'uppercase',
                           letterSpacing:'.04em' }}>{etiqueta}</span>
            <span style={{ fontSize:11, color:'#1B4332', fontWeight:600,
                           fontVariantNumeric:'tabular-nums' }}>{valor}</span>
          </div>
        ))}
      </div>
      </div>

      <div style={{ flex:1, minWidth:0, height:'100%', position:'relative' }}>
      {/* Toolbar de zoom flotante — overlay sobre el canvas, no afecta cálculo de dims */}
      <div style={{
        position:'absolute', top:8, right:8, zIndex:20,
        display:'flex', gap:2, background:'#fff', border:'1px solid #d1d5db',
        borderRadius:6, padding:2, boxShadow:'0 2px 6px rgba(0,0,0,0.08)',
      }} onMouseDown={e => e.stopPropagation()}>
        <button
          onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))}
          disabled={zoom <= 0.5} title="Alejar"
          style={{ ...btnZoom, opacity: zoom <= 0.5 ? 0.4 : 1 }}
        >−</button>
        <button
          onClick={() => setZoom(1)} title="Ajustar a viewport"
          style={{ ...btnZoom, minWidth:44 }}
        >{Math.round(zoom * 100)}%</button>
        <button
          onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))}
          disabled={zoom >= 3} title="Acercar"
          style={{ ...btnZoom, opacity: zoom >= 3 ? 0.4 : 1 }}
        >+</button>
      </div>

      <div ref={contenedorRef} style={{
        width:'100%', height:'100%',
        display:'flex',
        alignItems: zoom > 1 ? 'flex-start' : 'center',
        justifyContent: zoom > 1 ? 'flex-start' : 'center',
        overflow: zoom > 1 ? 'auto' : 'hidden',
        position:'relative',
      }}>
      <div
        onDragOver={onDragOver}
        onDrop={onDrop}
        style={{
          position:'relative',
          width: dims.ancho || '100%',
          height: dims.alto || '100%',
          background:'#fff',
          boxShadow:'0 4px 24px rgba(0,0,0,0.15)',
          // Grid visible con líneas suaves
          backgroundImage: `
            linear-gradient(to right, #f0f0f0 1px, transparent 1px),
            linear-gradient(to bottom, #f0f0f0 1px, transparent 1px)
          `,
          backgroundSize: `${dims.celdaAncho}px ${dims.celdaAlto}px`,
        }}
      >
        {bloques.map(b => {
          // Un bloque es "continuación" cuando otro bloque anterior (por fila,
          // luego columna) del mismo artículo ya existe en esta página. Los
          // bloques de continuación muestran solo el cuerpo restante, sin
          // repetir kicker/título/resumen/autor/imagen — para reflejar en el
          // canvas el mismo comportamiento del PDF.
          let esCont = false;
          if (b.articuloId) {
            const antes = bloques
              .filter(x => x.articuloId === b.articuloId && x.id !== b.id)
              .some(x =>
                x.filaInicio < b.filaInicio ||
                (x.filaInicio === b.filaInicio && x.columnaInicio < b.columnaInicio)
              );
            esCont = antes;
          }
          return (
          <BloqueVisual
            key={b.id}
            bloque={b}
            celdaAncho={dims.celdaAncho}
            celdaAlto={dims.celdaAlto}
            onFoco={(bl) => onFocoBloque?.(bl ? medidasDeBloque(bl, pagina) : null)}
            onMove={(e) => iniciarMove(e, b)}
            onResize={(e) => iniciarResize(e, b)}
            onEliminar={() => eliminarBloque(b.id)}
            plantillasArt={plantillasArt}
            onCambiarPlantilla={(pid) => cambiarPlantillaBloque(b, pid)}
            onActualizarBloque={(bloqueActualizado) =>
              setBloques(prev => prev.map(x => x.id === bloqueActualizado.id ? bloqueActualizado : x))
            }
            edicionId={pagina.edicionId}
            esContinuacion={esCont}
          />
          );
        })}
      </div>
      </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Medidas reales de un bloque a partir de la grilla de su página. El área útil
// del tabloide es 26 x 39,24 cm = 737 x 1112,2 pt, y 1 pt = 1 px a 72 dpi. Se
// descuentan los 8 px de padding interno que aplica el render del PDF.
// ────────────────────────────────────────────────────────────────────
const ANCHO_AREA_PT = 737.0, ALTO_AREA_PT = 1112.2, PADDING_PT = 8, PT_CM = 28.3465;

const NOMBRE_TIPO = {
  articulo: 'Artículo', pauta: 'Pauta',
  elemento_fijo: 'Elemento fijo', dinamico: 'Dinámico',
};

function medidasDeBloque(bloque, pagina) {
  const cols  = pagina?.columnas || 6;
  const filas = pagina?.filas || 24;
  const w = Math.round(bloque.columnasSpan * (ANCHO_AREA_PT / cols) - PADDING_PT);
  const h = Math.round(bloque.filasSpan    * (ALTO_AREA_PT  / filas) - PADDING_PT);
  const cm = `${(w / PT_CM).toFixed(1)} × ${(h / PT_CM).toFixed(1)} cm`;
  // El PDF de imprenta es un 4% mayor por el sangrado de 3 mm.
  const imp = `${Math.round(w * 1.041)}×${Math.round(h * 1.041)} px`;
  const tipo = NOMBRE_TIPO[bloque.tipo] || bloque.tipo;
  return `${tipo}: ${bloque.columnasSpan}×${bloque.filasSpan} · ${w}×${h} px · ${cm} · imprenta ${imp}`;
}

/** Texto de contexto de la página, que es lo que se muestra cuando el cursor no
 *  está sobre ningún bloque. El área de imprenta es un 4% mayor por el sangrado
 *  de 3 mm por lado. Todas las medidas en píxeles son a 72 dpi, donde 1 px = 1 pt. */
export function medidasDePagina(pagina) {
  if (!pagina) return [];
  const w = Math.round(ANCHO_AREA_PT), h = Math.round(ALTO_AREA_PT);
  return [
    ['Grilla',   `${pagina.columnas} × ${pagina.filas}`],
    ['Pantalla', `${w} × ${h} px`],
    ['Real',     `${(w / PT_CM).toFixed(1)} × ${(h / PT_CM).toFixed(1)} cm`],
    ['Imprenta', `${(w * 1.041 / PT_CM).toFixed(1)} × ${(h * 1.041 / PT_CM).toFixed(1)} cm`],
  ];
}

// Estilo compartido para los botones de la paleta lateral.
const btnAdd = {
  display:'flex', alignItems:'center', gap:6,
  padding:'6px 8px', fontSize:11, fontWeight:600,
  background:'#fff', border:'1.5px solid', borderRadius:4,
  cursor:'pointer',
};

const btnZoom = {
  minWidth:28, padding:'4px 8px', fontSize:13, fontWeight:700,
  background:'transparent', border:'none', color:'#1B4332', cursor:'pointer',
  borderRadius:4,
};

// ────────────────────────────────────────────────────────────────────
// PreviewMultimedia — vista del bloque de audio/video en el canvas. En papel
// este bloque imprime título, duración y un QR; aquí se muestra lo mismo en
// pequeño para que el editor vea qué va a salir impreso.
// ────────────────────────────────────────────────────────────────────
function PreviewMultimedia({ datos }) {
  const esVideo = datos?.tipo === 'video';
  const hayPieza = datos?.gsUri || datos?.url;
  return (
    <div style={{ padding:6, height:'100%', display:'flex', flexDirection:'column', gap:3, overflow:'hidden' }}>
      <div style={{ fontSize:8, fontWeight:700, color:'#c9a227', letterSpacing:'.06em' }}>
        {esVideo ? 'VIDEO' : 'AUDIO'}
      </div>
      <div style={{ fontSize:11, fontWeight:700, color:'#1B4332', lineHeight:1.2 }}>
        {datos?.titulo || 'Sin título'}
      </div>
      {datos?.autoria && <div style={{ fontSize:8, color:'#57534e' }}>{datos.autoria}</div>}
      {datos?.duracionTexto && (
        <div style={{ fontSize:8, color:'#57534e' }}>Duración {datos.duracionTexto}</div>
      )}
      <div style={{ marginTop:'auto', fontSize:8, fontStyle:'italic',
                    color: hayPieza ? '#166534' : '#b45309' }}>
        {hayPieza
          ? '▣ En el papel sale con código QR'
          : 'Falta subir el archivo — edita el bloque'}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// PreviewSopaLetras — vista reducida de la cuadrícula dentro del canvas de
// la maqueta. Solo sirve para ver cuánto ocupa y cómo queda distribuida; el
// trazado real está en el lector de la revista.
// ────────────────────────────────────────────────────────────────────
function PreviewSopaLetras({ datos }) {
  const grid = datos?.grid || [];
  const palabras = Object.keys(datos?.palabras || {}).sort();
  if (grid.length === 0) {
    return (
      <div style={{ fontSize:10, color:'#888', padding:6, fontStyle:'italic' }}>
        Sin palabras suficientes en los artículos de esta edición
      </div>
    );
  }
  const cols = datos.cols || grid[0].length;
  return (
    <div style={{ padding:4, height:'100%', display:'flex', flexDirection:'column', overflow:'hidden' }}>
      <div style={{ fontWeight:700, fontSize:9, color:'#1B4332', marginBottom:2 }}>SOPA DE LETRAS</div>
      <div style={{
        display:'grid', gridTemplateColumns:`repeat(${cols}, 1fr)`,
        border:'1px solid #c9a227', alignContent:'start', minHeight:0, flex:1,
      }}>
        {grid.flatMap((fila, f) => [...fila].map((ch, c) => (
          <div key={`${f}-${c}`} style={{
            aspectRatio:'1', display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:7, fontWeight:700, color:'#1a1a1a',
            background:(f+c)%2===0 ? '#faf7f0' : '#fff',
          }}>{ch}</div>
        )))}
      </div>
      <div style={{ fontSize:7, color:'#57534e', marginTop:3, lineHeight:1.3 }}>
        {palabras.length} palabras · {palabras.slice(0, 6).join(' · ')}{palabras.length > 6 ? ' …' : ''}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// PreviewDinamico — renderiza el contenido resuelto de un bloque
// dinámico. Llama al endpoint POST /contenido-dinamico/resolver
// pasando el elementoData actual y muestra el resultado según el
// tipoContenido (mas_noticias, indice, cabezote, etc.).
// ────────────────────────────────────────────────────────────────────
function PreviewDinamico({ edicionId, elementoData }) {
  const [contenido, setContenido] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!edicionId || !elementoData) return;
    let cancelado = false;
    (async () => {
      try {
        const r = await maquetaService.resolverDinamico(edicionId, elementoData);
        if (!cancelado) setContenido(r);
      } catch (e) {
        if (!cancelado) setError(e.message);
      }
    })();
    return () => { cancelado = true; };
  }, [edicionId, elementoData]);

  if (error) return <div style={{ fontSize:10, color:'#a00', padding:6 }}>Error: {error}</div>;
  if (!contenido) return <div style={{ fontSize:10, color:'#888', padding:6, fontStyle:'italic' }}>Cargando…</div>;

  const { tipoContenido, datos } = contenido;

  switch (tipoContenido) {
    case 'mas_noticias':
      return (
        <div style={{ padding:6, fontSize:8, overflow:'hidden' }}>
          <div style={{ fontWeight:700, fontSize:9, color:'#1B4332', borderBottom:'1px solid #1B4332', paddingBottom:2, marginBottom:4 }}>
            MÁS NOTICIAS
          </div>
          {(datos?.grupos || []).map((g, i) => (
            <div key={i} style={{ marginBottom:4 }}>
              {g.categoria && (
                <div style={{ fontSize:7, fontWeight:700, color: g.color || '#0369a1', marginTop:3 }}>
                  {g.categoria.toUpperCase()}
                </div>
              )}
              {(g.articulos || []).map((a, j) => (
                <div key={j} style={{ marginTop:2 }}>
                  <div style={{ fontWeight:600, color:'#1a1a1a', lineHeight:1.2 }}>{a.titulo}</div>
                  {a.autor && (
                    <div style={{ fontSize:6, color:'#666' }}>POR {a.autor.toUpperCase()}</div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    case 'titulares':
      return (
        <div style={{ padding:6, fontSize:8, overflow:'hidden' }}>
          <div style={{ fontWeight:700, fontSize:9, color:'#1B4332', borderBottom:'1px solid #1B4332', paddingBottom:2, marginBottom:4 }}>
            TITULARES
          </div>
          {(datos?.articulos || []).map((a, i) => (
            <div key={i} style={{ marginTop:3 }}>
              <span style={{ fontSize:7, fontWeight:700, color: a.color || '#0369a1' }}>{a.categoria} </span>
              <span style={{ fontWeight:600, color:'#1a1a1a' }}>{a.titulo}</span>
            </div>
          ))}
        </div>
      );
    case 'en_esta_edicion':
      return (
        <div style={{ padding:6, fontSize:8, overflow:'hidden' }}>
          <div style={{ fontWeight:700, fontSize:9, color:'#1B4332', borderBottom:'1px solid #1B4332', paddingBottom:2, marginBottom:4 }}>
            EN ESTA EDICIÓN
          </div>
          {(datos?.categorias || []).map((c, i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', marginTop:2 }}>
              <span style={{ fontWeight:600, color: c.color || '#1a1a1a' }}>{c.nombre}</span>
              <span style={{ color:'#666' }}>{c.conteo}</span>
            </div>
          ))}
        </div>
      );
    case 'indice':
      return (
        <div style={{ padding:6, fontSize:8, overflow:'hidden' }}>
          <div style={{ fontWeight:700, fontSize:9, color:'#1B4332', borderBottom:'1px solid #1B4332', paddingBottom:2, marginBottom:4 }}>
            ÍNDICE
          </div>
          {(datos?.entradas || []).map((e, i) => (
            <div key={i} style={{ marginTop:2, display:'flex', gap:4 }}>
              <span style={{ fontWeight:700, color:'#c9a227', minWidth:18 }}>P{e.pagina}</span>
              <span>
                {e.categoria && <span style={{ fontSize:7, fontWeight:700, color:'#0369a1' }}>{e.categoria} </span>}
                <span>{e.titulo}</span>
              </span>
            </div>
          ))}
        </div>
      );
    case 'cabezote':
      return (
        <div style={{ padding:6, textAlign:'center', display:'flex', flexDirection:'column', justifyContent:'center', height:'100%' }}>
          <div style={{ fontSize:16, fontWeight:900, color:'#1B4332', lineHeight:1 }}>
            {datos?.periodicoNombre}
          </div>
          {datos?.slogan && (
            <div style={{ fontSize:8, fontStyle:'italic', color:'#666', marginTop:2 }}>{datos.slogan}</div>
          )}
          <div style={{ fontSize:8, color:'#1a1a1a', marginTop:3 }}>
            {datos?.fecha}{datos?.edicionNumero ? ` · Edición Nº ${datos.edicionNumero}` : ''}
          </div>
        </div>
      );
    case 'creditos':
      return (
        <div style={{ padding:6, fontSize:8, overflow:'hidden' }}>
          <div style={{ fontWeight:700, fontSize:9, color:'#1B4332', borderBottom:'1px solid #1B4332', paddingBottom:2, marginBottom:4 }}>
            CRÉDITOS
          </div>
          {datos?.director && (
            <div style={{ marginTop:2 }}>
              <span style={{ fontWeight:700 }}>Director: </span>{datos.director}
            </div>
          )}
          {datos?.colaboradores?.length > 0 && (
            <div style={{ marginTop:4 }}>
              <span style={{ fontWeight:700 }}>Colaboradores: </span>
              {datos.colaboradores.join(', ')}
            </div>
          )}
        </div>
      );
    case 'sopa_letras':
      return <PreviewSopaLetras datos={datos} />;
    case 'multimedia':
      return <PreviewMultimedia datos={datos} />;
    default:
      return <div style={{ padding:6, fontSize:10, color:'#888' }}>Tipo desconocido</div>;
  }
}

function BloqueVisual({ bloque, celdaAncho, celdaAlto, onMove, onResize, onEliminar,
                        plantillasArt = [], onCambiarPlantilla, onActualizarBloque, onFoco,
                        edicionId, esContinuacion = false }) {
  const esArticulo = bloque.tipo === 'articulo';
  const esPauta    = bloque.tipo === 'pauta';
  const esFijo     = bloque.tipo === 'elemento_fijo';
  const esDinamico = bloque.tipo === 'dinamico';
  const asignado   = (esArticulo && bloque.articuloId) || (esPauta && bloque.pautaId);

  // Plantilla actual (si tiene). El PDF respeta este layout.
  const plantillaActual = plantillasArt.find(p => p.id === bloque.plantillaArticuloId);

  // Estado de upload para elemento_fijo
  const [subiendo, setSubiendo] = useState(false);
  const [editandoTexto, setEditandoTexto] = useState(false);
  const [editandoMultimedia, setEditandoMultimedia] = useState(false);
  const [borradorTexto, setBorradorTexto] = useState('');
  const fileInputRef = useRef(null);

  // Parsear ElementoData si es JSON (elemento_fijo con imagen)
  let fijoData = null;
  if (esFijo && bloque.elementoData) {
    try { fijoData = JSON.parse(bloque.elementoData); } catch { /* texto plano */ }
  }
  // URL para el preview en el canvas, en este orden:
  //   1) signedUrlPreview: URL firmada temporal, existe si acabas de subir en
  //      esta sesión (no se persiste en el bloque en BD).
  //   2) imagenUrl (gs://...) proxied vía endpoint del backend: siempre
  //      funciona incluso tras recargar la página.
  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1';
  // El elemento fijo admite imagen, texto o ambos: el render del PDF pinta la
  // imagen arriba y el texto debajo cuando vienen los dos.
  const fijoTexto = fijoData
    ? (fijoData.texto ?? null)
    : (bloque.elementoData || null);   // elemento_data en texto plano

  // Persiste en el backend. onActualizarBloque solo refresca el estado local de
  // React: la subida de imagen se guardaba sola porque su endpoint escribe el
  // elemento_data del lado del servidor, pero el texto no tiene endpoint propio
  // y hay que mandarlo con el update del bloque.
  const guardarTextoFijo = async (valor) => {
    const limpio = (valor || '').trim();
    const base = fijoData && typeof fijoData === 'object' ? { ...fijoData } : {};
    if (limpio) base.texto = limpio; else delete base.texto;
    const elementoData = Object.keys(base).length ? JSON.stringify(base) : null;
    try {
      const actualizado = await maquetaService.actualizarBloque(bloque.id, {
        columnaInicio: bloque.columnaInicio, filaInicio: bloque.filaInicio,
        columnasSpan: bloque.columnasSpan, filasSpan: bloque.filasSpan,
        articuloId: bloque.articuloId ?? null, pautaId: bloque.pautaId ?? null,
        elementoData, estilo: bloque.estilo ?? null,
        plantillaArticuloId: bloque.plantillaArticuloId ?? null,
      });
      onActualizarBloque?.(actualizado);
      window.dispatchEvent(new CustomEvent('app:toast', { detail: 'Texto guardado' }));
    } catch (err) {
      alert('No se pudo guardar el texto: ' + err.message);
    }
  };

  const esMultimedia = (() => {
    if (!esDinamico || !bloque.elementoData) return false;
    try { return JSON.parse(bloque.elementoData).tipoContenido === 'multimedia'; }
    catch { return false; }
  })();

  const fijoSignedUrl = fijoData?.signedUrlPreview
    || (fijoData?.imagenUrl?.startsWith('gs://')
        ? `${apiBase}/storage/proxy?gsUri=${encodeURIComponent(fijoData.imagenUrl)}`
        : null);

  const handleSubirImagenFijo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      // Resize antes de subir (igual que artículos)
      let fileToUpload = file;
      try {
        const resized = await resizeImage(file);
        if (resized.size < file.size) fileToUpload = resized;
      } catch { /* sube original si falla resize */ }

      // Subir usando el mismo servicio que funciona en artículos
      const fd = new FormData(); fd.append('archivo', fileToUpload);
      const token = localStorage.getItem('newspaperToken');
      const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1';
      const resp = await fetch(
        `${apiBase}/storage/maqueta/bloques/${bloque.id}/imagen?objectFit=contain`,
        { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body: fd }
      );
      if (!resp.ok) throw new Error(`Error ${resp.status}`);
      const data = await resp.json();

      // Obtener URL visualizable en este orden:
      //   1) signedUrl que devuelve el endpoint del upload (ideal, requiere IAM)
      //   2) getSignedUrl endpoint (mismo requisito IAM)
      //   3) proxy endpoint del backend, que descarga con credenciales ADC.
      //      Este último SIEMPRE funciona mientras la SA tenga acceso al bucket.
      let previewUrl = data.signedUrl;
      if (!previewUrl && data.gsUri) {
        try {
          previewUrl = await storageService.getSignedUrl(data.gsUri, 24);
        } catch { /* sigue al fallback */ }
      }
      if (!previewUrl && data.gsUri) {
        const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/v1';
        previewUrl = `${apiBase}/storage/proxy?gsUri=${encodeURIComponent(data.gsUri)}`;
      }

      // Actualizar el bloque localmente para mostrar preview
      onActualizarBloque?.({
        ...bloque,
        elementoData: JSON.stringify({
          tipo: 'imagen',
          imagenUrl: data.gsUri,
          objectFit: 'contain',
          signedUrlPreview: previewUrl,
        }),
      });
      window.dispatchEvent(new CustomEvent('app:toast', { detail: 'Imagen subida' }));
    } catch (err) {
      alert('No se pudo subir la imagen: ' + err.message);
    } finally {
      setSubiendo(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const bg = !asignado && !esFijo && !esDinamico ? '#f9fafb'
           : esPauta ? '#dbeafe'
           : esFijo  ? '#fefce8'
           : esDinamico ? '#f5f3ff'
           : '#fff';
  const border = esPauta ? '2px solid #1e40af'
               : esFijo  ? '2px dashed #78350f'
               : esDinamico ? '2px dashed #7c3aed'
               : !asignado ? '2px dashed #c9a227'
               : '2px solid #1B4332';

  return (
    <div
      onMouseEnter={() => onFoco?.(bloque)}
      onMouseLeave={() => onFoco?.(null)}
      style={{
      position:'absolute',
      left: (bloque.columnaInicio - 1) * celdaAncho,
      top:  (bloque.filaInicio    - 1) * celdaAlto,
      width:  bloque.columnasSpan * celdaAncho,
      height: bloque.filasSpan    * celdaAlto,
      background: bg, border,
      overflow:'hidden', boxSizing:'border-box',
      display:'flex', flexDirection:'column',
    }}>
      {/* Handle superior con drag para mover */}
      <div
        onMouseDown={onMove}
        style={{
          padding:'2px 6px', background:'rgba(0,0,0,0.05)',
          cursor:'grab', display:'flex', alignItems:'center', gap:6,
          fontSize:10, color:'#666', userSelect:'none',
        }}
      >
        <Move size={10} />
        <span style={{ flex:1, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
          {esArticulo ? (bloque.articuloTitulo || '[Slot artículo]')
           : esPauta   ? (bloque.pautaNombre    || '[Slot pauta]')
           : esDinamico ? (() => {
               try {
                 const d = JSON.parse(bloque.elementoData || '{}');
                 const nombres = {
                   mas_noticias: 'Más noticias', titulares: 'Titulares',
                   en_esta_edicion: 'En esta edición', indice: 'Índice',
                   cabezote: 'Cabezote', creditos: 'Créditos',
                   sopa_letras: 'Sopa de letras', multimedia: 'Audio o video',
                 };
                 return nombres[d.tipoContenido] || '[Dinámico]';
               } catch { return '[Dinámico]'; }
             })()
           : '[Elemento fijo]'}
        </span>
        <button onClick={(e) => { e.stopPropagation(); onEliminar(); }}
          style={{ background:'transparent', border:'none', cursor:'pointer', color:'#dc2626', padding:0 }}>
          <X size={12} />
        </button>
      </div>

      {/* Segunda fila: selector de plantilla de artículo (solo si hay artículo
          asignado). Cambiar la plantilla actualiza el bloque en el backend y
          el PDF se genera con ese layout. Con "Estándar" (null) usa el render
          apilado por defecto. */}
      {esArticulo && bloque.articuloId && plantillasArt.length > 0 && (
        <div
          onMouseDown={(e) => e.stopPropagation()}
          style={{
            padding:'2px 6px', background:'rgba(201,162,39,0.08)',
            borderTop:'1px solid rgba(201,162,39,0.25)',
            display:'flex', alignItems:'center', gap:4,
            fontSize:10, color:'#78350f',
          }}
          title="Layout interno del artículo en el PDF"
        >
          <span style={{ fontWeight:600, whiteSpace:'nowrap' }}>Layout:</span>
          <select
            value={bloque.plantillaArticuloId || ''}
            onChange={(e) => onCambiarPlantilla?.(e.target.value ? parseInt(e.target.value, 10) : null)}
            onClick={(e) => e.stopPropagation()}
            style={{
              flex:1, minWidth:0, padding:'1px 2px', fontSize:10,
              border:'1px solid #d1a94a', borderRadius:2, background:'#fff',
            }}
          >
            <option value="">Estándar</option>
            {plantillasArt.map(p => (
              <option key={p.id} value={p.id}>{p.nombre}</option>
            ))}
          </select>
        </div>
      )}

      {/* Contenido */}
      <div style={{ flex:1, padding:6, overflow:'hidden', fontSize:11, minHeight:0, position:'relative' }}>
        {/* Preview del layout de la plantilla (si hay). Se dibuja como grid
            translúcido con recuadros por elemento — el editor ve dónde va cada
            pieza sin necesidad de generar el PDF. */}
        {esArticulo && plantillaActual && plantillaActual.elementos?.length > 0 && (
          <PreviewLayoutPlantilla
            plantilla={plantillaActual}
            bloque={bloque}
            esContinuacion={esContinuacion}
          />
        )}
        {/* Contenido normal (título + resumen) — solo si NO hay plantilla,
            para no competir visualmente con el preview. */}
        {esArticulo && bloque.articuloTitulo && !plantillaActual && (
          <>
            <div style={{ fontWeight:700, color:'#1B4332', lineHeight:1.2, marginBottom:4 }}>
              {bloque.articuloTitulo}
            </div>
            {bloque.articuloResumen && (
              <div style={{ color:'#555', fontSize:10, lineHeight:1.3, overflow:'hidden', display:'-webkit-box', WebkitLineClamp:6, WebkitBoxOrient:'vertical' }}>
                {bloque.articuloResumen}
              </div>
            )}
          </>
        )}
        {esPauta && bloque.pautaId && (
          <div style={{ height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:4, gap:4 }}>
            {bloque.pautaImagenUrl ? (
              <img
                src={bloque.pautaImagenUrl}
                alt={bloque.pautaAnunciante || 'Publicidad'}
                style={{ maxWidth:'100%', maxHeight:'70%', objectFit:'contain' }}
              />
            ) : null}
            <div style={{ fontSize:9, color:'#1e40af', fontWeight:600, textAlign:'center' }}>
              {bloque.pautaAnunciante || bloque.pautaNombre || 'PAUTA'}
            </div>
          </div>
        )}
        {esPauta && !bloque.pautaId && (
          <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#1e40af', fontWeight:600, fontSize:11 }}>
            Slot de pauta
          </div>
        )}

        {/* ── Elemento fijo: mini-editor de imagen gráfica ── */}
        {esFijo && (
          <div
            onMouseDown={e => e.stopPropagation()}
            style={{ height:'100%', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:6, padding:6 }}
          >
            {editandoTexto && createPortal(
              /* El editor va en un portal al <body> y no dentro del bloque: en
                 un bloque bajo, como una cabecera de 2 filas, el área de texto
                 quedaba aplastada a una línea y no se podía escribir. Fuera del
                 árbol del lienzo tampoco depende del tamaño ni del orden de
                 apilamiento de los bloques vecinos. */
              <div
                onMouseDown={() => setEditandoTexto(false)}
                style={{ position:'fixed', inset:0, zIndex:2000, background:'rgba(0,0,0,.45)',
                         display:'flex', alignItems:'center', justifyContent:'center' }}
              >
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  style={{ background:'#fff', borderRadius:8, padding:18, width:460, maxWidth:'92vw',
                           boxShadow:'0 12px 40px rgba(0,0,0,.3)' }}
                >
                  <h3 style={{ margin:'0 0 6px', fontSize:15 }}>Texto del elemento fijo</h3>
                  <p style={{ margin:'0 0 10px', fontSize:11, color:'#78716c', lineHeight:1.4 }}>
                    Para un encabezado, escribe el <strong>título en la primera línea</strong> y
                    el <strong>subtítulo en la segunda</strong>. En el PDF la primera sale grande
                    y la segunda más pequeña debajo. Con una sola línea se imprime como texto normal.
                  </p>
                  <textarea
                    autoFocus
                    value={borradorTexto}
                    onChange={e => setBorradorTexto(e.target.value)}
                    placeholder={'CLASIFICADOS\nCompra · Venta · Servicios · Empleo'}
                    rows={6}
                    style={{ width:'100%', fontSize:13, padding:8, borderRadius:4, boxSizing:'border-box',
                             border:'1px solid #d6c9a8', fontFamily:'inherit', resize:'vertical' }}
                  />
                  <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:12 }}>
                    <button
                      onClick={() => setEditandoTexto(false)}
                      style={{ padding:'6px 14px', fontSize:12, background:'#e5e7eb',
                               color:'#374151', border:'none', borderRadius:4, cursor:'pointer' }}>
                      Cancelar
                    </button>
                    <button
                      onClick={async () => { await guardarTextoFijo(borradorTexto); setEditandoTexto(false); }}
                      style={{ padding:'6px 14px', fontSize:12, fontWeight:600, background:'#166534',
                               color:'#fff', border:'none', borderRadius:4, cursor:'pointer' }}>
                      Guardar
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}
            {(
              <>
                {fijoSignedUrl && (
                  <img
                    src={fijoSignedUrl}
                    alt="Elemento gráfico"
                    style={{ maxWidth:'100%', maxHeight: fijoTexto ? 'calc(100% - 56px)' : 'calc(100% - 28px)',
                             objectFit:'contain', borderRadius:2 }}
                  />
                )}
                {fijoTexto && (
                  <div style={{ fontSize:10, color:'#44403c', textAlign:'center',
                                overflow:'hidden', whiteSpace:'pre-wrap', lineHeight:1.25 }}>
                    {fijoTexto}
                  </div>
                )}
                {!fijoSignedUrl && !fijoTexto && (
                  <div style={{ color:'#92400e', fontSize:10, fontStyle:'italic', textAlign:'center' }}>
                    Sin contenido · sube una imagen o escribe un texto
                  </div>
                )}
              </>
            )}
            {/* Input oculto + botón */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/svg+xml"
              style={{ display:'none' }}
              onChange={handleSubirImagenFijo}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={subiendo}
              style={{
                padding:'3px 8px', fontSize:10, fontWeight:600,
                background: subiendo ? '#e5e7eb' : '#78350f', color: subiendo ? '#6b7280' : '#fff',
                border:'none', borderRadius:3, cursor: subiendo ? 'not-allowed' : 'pointer',
              }}
            >
              {subiendo ? 'Subiendo…' : fijoSignedUrl ? '✎ Cambiar imagen' : '⬆ Subir imagen'}
            </button>
            {!editandoTexto && (
              <button
                onClick={() => { setBorradorTexto(fijoTexto || ''); setEditandoTexto(true); }}
                style={{
                  padding:'3px 8px', fontSize:10, fontWeight:600,
                  background:'#fff', color:'#78350f',
                  border:'1px solid #78350f', borderRadius:3, cursor:'pointer',
                }}
              >
                {fijoTexto ? '✎ Editar texto' : '✎ Escribir texto'}
              </button>
            )}
          </div>
        )}

        {/* ── Bloque dinámico: preview auto-generado del backend ── */}
        {esDinamico && (
          <div style={{ height:'100%', overflow:'hidden', position:'relative' }}>
            <PreviewDinamico edicionId={edicionId} elementoData={bloque.elementoData} />
            {esMultimedia && (
              <button
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => setEditandoMultimedia(true)}
                style={{
                  position:'absolute', right:4, bottom:4, fontSize:10, fontWeight:600,
                  padding:'3px 8px', border:'none', borderRadius:3,
                  background:'#166534', color:'#fff', cursor:'pointer',
                }}
              >✎ Editar pieza</button>
            )}
          </div>
        )}
        {editandoMultimedia && (
          <EditorMultimedia
            bloque={bloque}
            edicionId={edicionId}
            onCerrar={() => setEditandoMultimedia(false)}
            onGuardado={(b) => onActualizarBloque?.(b)}
          />
        )}

        {!asignado && !esFijo && !esDinamico && (
          <div style={{ height:'100%', display:'flex', alignItems:'center', justifyContent:'center', color:'#c9a227', fontStyle:'italic' }}>
            {bloque.columnasSpan}×{bloque.filasSpan} · sin contenido
          </div>
        )}
      </div>

      {/* Handle de resize esquina inferior-derecha */}
      <div
        onMouseDown={onResize}
        style={{
          position:'absolute', right:0, bottom:0, width:12, height:12,
          background:'#1B4332', cursor:'nwse-resize',
        }}
        title="Arrastra para redimensionar"
      />
    </div>
  );
}

/**
 * Preview visual del layout de una plantilla dentro del bloque. Dibuja cada
 * elemento como un recuadro traslúcido con su tipo y (si aplica) contenido
 * real del artículo (título, categoría, resumen). Da al editor una vista
 * previa fiel de cómo saldrá el bloque en el PDF sin tener que generarlo.
 */
function PreviewLayoutPlantilla({ plantilla, bloque, esContinuacion = false }) {
  const cols = plantilla.columnas || 12;
  const filas = plantilla.filas   || 12;
  // En bloques de continuación no se pintan kicker/título/resumen/autor/imagen.
  const tiposSuprimidos = new Set(['categoria', 'titulo', 'resumen', 'autor', 'imagen']);
  const elementosVisibles = esContinuacion
    ? plantilla.elementos.filter(el => !tiposSuprimidos.has(el.tipo))
    : plantilla.elementos;
  return (
    <div style={{ position:'absolute', inset:0, padding:2 }}>
      <div style={{ position:'relative', width:'100%', height:'100%' }}>
        {elementosVisibles.map((el, i) => {
          const meta = COLORES_ELEMENTO[el.tipo] || { color:'#666', label: el.tipo };
          return (
            <div
              key={el.id ?? `el-${i}`}
              style={{
                position:'absolute',
                left:  `${((el.columnaInicio - 1) / cols) * 100}%`,
                top:   `${((el.filaInicio    - 1) / filas) * 100}%`,
                width: `${(el.columnasSpan / cols) * 100}%`,
                height:`${(el.filasSpan    / filas) * 100}%`,
                background: `${meta.color}0F`,
                border: `1px dashed ${meta.color}80`,
                boxSizing:'border-box',
                overflow:'hidden',
                display:'flex', alignItems:'flex-start', justifyContent:'flex-start',
                padding:2,
                fontSize:8, color: meta.color, fontWeight:600,
                textTransform: el.tipo === 'categoria' ? 'uppercase' : 'none',
              }}
              title={`${meta.label} · ${el.columnasSpan}×${el.filasSpan}`}
            >
              {contenidoElemento(el.tipo, bloque, plantilla)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const COLORES_ELEMENTO = {
  titulo:     { color:'#111',    label:'Título' },
  categoria:  { color:'#c9a227', label:'Categoría' },
  resumen:    { color:'#4a5555', label:'Resumen' },
  imagen:     { color:'#0369a1', label:'Imagen' },
  cuerpo:     { color:'#374151', label:'Cuerpo' },
  autor:      { color:'#7c3aed', label:'Autor' },
  referencia: { color:'#c9a227', label:'Continúa en pág.' },
  separador:  { color:'#999',    label:'—' },
  espacio:    { color:'#ccc',    label:'·' },
};

function contenidoElemento(tipo, b, plantilla) {
  switch (tipo) {
    case 'categoria': return b.articuloCategoria || 'Categoría';
    case 'titulo':    return b.articuloTitulo    || 'Título del artículo';
    case 'resumen':   return b.articuloResumen   || 'Resumen';
    case 'autor':     return b.articuloAutor ? `POR ${b.articuloAutor}` : 'Autor';
    case 'imagen':
      {
        const gsUri = b.articuloImagenUrl;
        const httpsUrl = gsUri && gsUri.startsWith('gs://')
          ? `https://storage.googleapis.com/${gsUri.substring(5)}`
          : gsUri;
        return httpsUrl
          ? <img src={httpsUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
          : '📷';
      }
    case 'cuerpo': {
      const cols = plantilla?.columnasCuerpo || 1;
      const preview = (b.articuloResumen || b.articuloCuerpo || 'Cuerpo del artículo').substring(0, 400);
      return (
        <div style={{
          columnCount: cols,
          columnGap: 4,
          fontWeight: 400, color: '#374151', fontSize: 7,
          lineHeight: 1.3, textAlign: 'justify',
          width: '100%', height: '100%', overflow: 'hidden',
        }}>{preview}</div>
      );
    }
    case 'referencia': return '▶ Continúa en pág. …';
    case 'separador': return '';
    case 'espacio':   return '';
    default:          return tipo;
  }
}
